import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom'
import { TodoUser } from 'api/clients/TodoClient'
import { useLocalDebouncedFn } from 'hooks/useLocalDebouncedFn'
import cx from 'classnames'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useReactNodeView } from 'common/Prose/nodeViews/ReactNodeView/ReactNodeViewContext'
import { CollabItemInlineTooltip } from 'components/CollabItemInlineTooltip/CollabItemInlineTooltip'
import { TASKS_VIEW } from 'components/DocRightRail/Constants'
import { DueDatePicker } from 'components/DueDatePicker/DueDatePicker'
import { TodoAssigneePicker } from 'components/TodoAssigneePicker/TodoAssigneePicker'
import { TodoFormValues } from 'components/TodoForm/Types'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { Node as ProseNode } from 'prosemirror-model'
import { serializeFragmentToHTMLString, serializeFragmentToPlainTextString } from 'proseShared/utils'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setActiveTodo, setHighlightedTodo } from 'redux/actions/todos'
import { useDocStore } from 'stores/DocStore'
import { updateListItemMarkerStyles } from '../utils'
import Styles from './TodoListItem.module.scss'
import { useAuth } from 'hooks/UseAuth'
import { ArticleView } from 'types'
import { TodoCheckbox } from './TodoCheckbox/TodoCheckbox'

export const TodoListItem = observer(
  React.forwardRef<HTMLDivElement, any>(({ children }, contentDomRef) => {
    const { nodeView, node, view, getPos } = useReactNodeView()
    const docStore = useDocStore()
    const auth = useAuth()
    const [showAssigneePicker, setShowAssigneePicker] = useState(false)
    const [showDueDatePicker, setShowDueDatePicker] = useState(false)
    const dispatch = useDispatch()
    const todoId = node?.attrs.todoId
    const checked = node?.attrs.checked
    const uuid = node?.attrs.uuid
    const depth = node?.attrs.depth
    const todo = todoId ? docStore.todos.find((t) => t.id === todoId) : null
    const isActive = computed(() => (todo?.id ? docStore.rootStore.todos.selected?.id === todo?.id : false)).get()
    const isHighlighted = computed(() =>
      todo?.id ? docStore.rootStore.todos.highlighted?.id === todo?.id : false,
    ).get()
    const ref = useRef<HTMLDivElement | null>(null)
    // We always give precedence to the todo record, if present
    const completed = todo ? todo.completed_at : checked
    const readOnly = docStore.articleView !== ArticleView.Editing || !auth

    const debouncedUpsert = useLocalDebouncedFn(
      (node: ProseNode) => {
        if (!todo?.id) {
          return
        }
        const currentHTMLString = serializeFragmentToHTMLString(node.content, view?.state.schema)
        const currentTextString = serializeFragmentToPlainTextString(node.content)
        const payload = {
          title: currentTextString,
          htmlTitle: currentHTMLString,
        }

        docStore.rootStore.todos.update(todo?.id, payload)
      },
      500,
      undefined,
      [todo?.id],
    )

    useEffect(() => {
      if (node) debouncedUpsert(node)
    }, [debouncedUpsert, node])

    useEffect(() => {
      if (nodeView) {
        nodeView.stopEvent = (e: Event) => {
          if (e instanceof ClipboardEvent || (e.target as HTMLElement).closest('[data-node-type="image"]')) {
            return false
          }
          return true
        }
        nodeView.ignoreMutation = ({ target, type }: MutationRecord | { target: Element; type: 'selection' }) => {
          const isMarkerStyleUpdate = type === 'attributes' && target === nodeView.dom

          return !nodeView.contentDOM?.contains(target) || isMarkerStyleUpdate
        }
      }
    }, [nodeView, view, getPos])

    useEffect(() => {
      const domNode = nodeView?.dom
      if (domNode) {
        domNode.dataset.depth = depth
        domNode.dataset.checked = completed ? 'true' : 'false'
      }
    }, [completed, depth, nodeView])

    useLayoutEffect(() => {
      if (!nodeView) return
      const { contentDOM, dom } = nodeView
      if (view && contentDOM && dom) {
        setTimeout(() => updateListItemMarkerStyles(view, getPos(), contentDOM, dom))
      }
    }, [nodeView, getPos, view])

    /* -------------------------------------------------------------------------------------------------
     * Author tooltip positioning
     * -----------------------------------------------------------------------------------------------*/

    const { x, y, reference, floating, strategy } = useFloating({
      strategy: 'fixed',
      placement: 'top',
      whileElementsMounted: (reference, floating, update) => {
        return autoUpdate(reference, floating, update, {
          animationFrame: true,
        })
      },
      middleware: [offset(10), flip(), shift({ padding: 8 })],
    })

    const authorTooltipStyle = {
      top: 0,
      left: 0,
      transform: `translate(${Math.round(x || 0)}px,${Math.round(y || 0)}px)`,
      zIndex: 1,
      position: strategy,
    }

    useLayoutEffect(() => {
      reference(ref.current)
    }, [ref.current]) // eslint-disable-line react-hooks/exhaustive-deps

    /* -------------------------------------------------------------------------------------------------
     * Autoscroll and selection effects
     * -----------------------------------------------------------------------------------------------*/

    useEffect(() => {
      if (ref.current && isActive) {
        ref.current.scrollIntoViewIfNeeded(true)
      }
    }, [isActive])

    /* -------------------------------------------------------------------------- */

    const updateAttributes = (updatedAttrs: Record<string, any>) => {
      if (!view || !getPos) return

      const newAttrs = { ...node?.attrs, ...updatedAttrs }
      const tx = view.state.tr.setNodeMarkup(getPos(), undefined, newAttrs)
      view.dispatch(tx)
    }

    const handleAssigneeChange = async (assignee: TodoUser | null) => {
      if (!todo?.id) {
        createTodo({ assignee })
      } else {
        docStore.rootStore.todos.update(todo?.id, { assigneeId: assignee?.id || null })
      }
    }

    const handleDueDateChange = async (dueDate: string | null) => {
      if (!todo?.id) {
        createTodo({ dueDate })
      } else {
        docStore.rootStore.todos.update(todo?.id, { dueDate: dueDate })
      }
    }

    const createTodo = async ({ assignee = null, dueDate = null }: Partial<TodoFormValues>) => {
      if (!node || !view) return
      // TODO replace with a getter for current node
      const plainTextTitle = serializeFragmentToPlainTextString(node.content)
      const htmlTitle = serializeFragmentToHTMLString(node.content, view.state.schema)
      const payload = {
        title: plainTextTitle,
        htmlTitle: htmlTitle,
        description: '',
        htmlDescription: '',
        assigneeId: assignee?.id,
        dueDate: dueDate,
        mentionedIds: [],
      }
      const { uuid, id } = await docStore.rootStore.todos.create(docStore.articleId, payload, true)
      updateAttributes({ uuid, todoId: id })
    }

    const handleClick = () => {
      if (!todo || completed) return

      docStore.rightRailStore.setActiveTab(TASKS_VIEW)
      docStore?.rightRailStore.openSection('todos')
      docStore.rootStore.todos.select(todo.id)
    }

    const handleMouseEnter = () => {
      if (!todo || completed) return
      docStore.rootStore.todos.highlight(todo.id)
    }

    const handleMouseLeave = () => {
      if (completed) return
      docStore.rootStore.todos.highlight(undefined)
    }

    const handleCheckboxClick = async (e: React.MouseEvent) => {
      updateAttributes({ checked: !completed })
      e.stopPropagation()

      if (!todo || !auth) {
        return
      }
      dispatch(setHighlightedTodo(undefined))
      dispatch(setActiveTodo(undefined))

      try {
        updateAttributes({ checked: !todo.completed_at })
        await docStore.rootStore.todos.toggleCompleted(todo.id)
      } catch (e) {
        showErrorMessageToast('There was a problem updating the task, please try again.')
        updateAttributes({ checked: completed })
      }
    }

    return (
      <div
        className={cx(Styles.container, { [Styles.todoRecord]: !!todo })}
        data-uuid={uuid}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={ref}
      >
        {todo && isHighlighted && (
          <div style={authorTooltipStyle} ref={floating}>
            <CollabItemInlineTooltip userId={todo.creator.id} />
          </div>
        )}
        <div className={Styles.markerColumn} contentEditable={false}>
          <TodoCheckbox
            className={Styles.checkbox}
            checked={completed}
            onClick={handleCheckboxClick}
            readOnly={readOnly}
          />
        </div>
        <div className={cx(Styles.contentWrapper, Styles.contentDOM)} ref={contentDomRef} />
        {!completed && docStore.isMainLayer && auth && (
          <div
            onMouseDown={(e) => e.preventDefault()}
            className={cx(Styles.actions, {
              [Styles.showOnHover]: !todo && !showAssigneePicker && !showDueDatePicker,
            })}
            contentEditable={false}
          >
            <div
              className={cx({
                [Styles.assigneePickerContainer]: todo?.assignee,
              })}
            >
              <TodoAssigneePicker
                size={todo?.assignee ? 'xl' : 'xs'}
                value={todo?.assignee || null}
                onChange={handleAssigneeChange}
                className={Styles.assigneePicker}
                onOpenChange={(open) => setShowAssigneePicker(open)}
                placement="left-start"
              />
            </div>
            <DueDatePicker
              size="sm"
              value={todo?.due_date || null}
              onChange={handleDueDateChange}
              onOpenChange={(open) => setShowDueDatePicker(open)}
            />
          </div>
        )}
      </div>
    )
  }),
)

export const ReadTodoListItem = React.forwardRef<HTMLDivElement, any>(({ children }, contentDomRef) => {
  const { node } = useReactNodeView()
  const checked = node?.attrs.checked

  return (
    <div className={Styles.container}>
      <div className={Styles.markerColumn} contentEditable={false}>
        <TodoCheckbox checked={checked} disabled className={Styles.checkbox} readOnly />
      </div>
      <div className={cx(Styles.contentWrapper, Styles.contentDOM)} ref={contentDomRef} />
    </div>
  )
})
